import config from "@Config/index";
import { AppState } from "@Store/index";
import { useSelector } from "react-redux";
import useAbTestVariant from "hooks/useAbTestVariant";
import useIsDesktop from "hooks/useIsDesktop";
import { useResponsiveness } from "hooks/useResponsiveness";
import useWhitelabel from "hooks/useWhitelabel";

// The variant is assigned at the CDN, like scholarship_variant. CloudFront's
// abExperimentViewerRequest Lambda@Edge picks it (or reads this cookie) before the cache lookup and
// sends it as the `x-feature-overview_gallery` header, which is in the /places/* cache key; the
// viewer-response Lambda then sets this cookie. abTestMiddleware puts it in the store, so the
// server renders the visitor's variant and the client (reading the cookie) agrees.
export const OVERVIEW_GALLERY_AB_TEST = "overview_gallery";
// Same edge as `applies-above(lg)` and `isAbove("lg")`: 1200 itself gets the new fold.
export const OVERVIEW_GALLERY_MIN_WIDTH = 1200;
// `media` for each fold's hero preload, matching overviewLayout.module.scss, so a new-variant
// visitor downloads only the hero of the fold that shows.
export const OVERVIEW_NEW_FOLD_MEDIA = `(min-width: ${OVERVIEW_GALLERY_MIN_WIDTH}px)`;
export const OVERVIEW_OLD_FOLD_MEDIA = `(max-width: ${OVERVIEW_GALLERY_MIN_WIDTH - 1}px)`;

export type OverviewGalleryVariant = "new" | "control";

/**
 * The arm a user is exposed to: `"new"` / `"control"` only at ≥1200, where the two arms differ;
 * `null` otherwise (not in the experiment, below 1200, or before mount). Events are tagged with
 * it and experiment_viewed fires for it, so only exposed users are counted (FR-18).
 */
export type OverviewGalleryArm = OverviewGalleryVariant | null;

/**
 * Which top fold(s) to render:
 * - `"both"`: new variant, server render and first client render. The server can't see the width,
 *   so it renders both folds and CSS shows the new one at ≥1200 and the old one below
 *   (overviewLayout.module.scss); the first client render must match it for hydration.
 * - `"new"` / `"old"`: the one fold to render (control, or new after mount once the width is
 *   known).
 */
export type OverviewGalleryMode = "both" | "new" | "old";

export interface OverviewGalleryLayout {
  // The CDN-assigned variant; same on server and client, so it is safe to render from.
  variant: OverviewGalleryVariant | null;
  mode: OverviewGalleryMode;
  arm: OverviewGalleryArm;
}

const useOverviewGalleryArm = (): OverviewGalleryLayout => {
  const isDesktop = useIsDesktop();
  const { isWhitelabel } = useWhitelabel();
  // null during SSR and the first client render, then the real width (and on resize).
  const { windowWidth } = useResponsiveness();
  const assigned = useAbTestVariant(OVERVIEW_GALLERY_AB_TEST);
  // Set from the logged-in user on the server and carried to the client, so both render the same.
  // Logged-in pages are never publicly cached, so this can't leak to other visitors.
  const isInternalUser = useSelector(({ appData }: AppState) => appData.isInternalUser);

  // China (no Lambda on its CDN) and internal users always get the new gallery, whatever their
  // group, and are kept out of the experiment (no arm, so no experiment_viewed).
  const isOutsideExperiment = config.IS_CHINA_APP || isInternalUser;
  let variant: OverviewGalleryVariant | null = null;
  if (isDesktop && !isWhitelabel) {
    if (isOutsideExperiment) variant = "new";
    else if (assigned === "new" || assigned === "control") variant = assigned;
  }
  if (!variant) return { variant, mode: "old", arm: null };

  const isWide = windowWidth === null ? null : windowWidth >= OVERVIEW_GALLERY_MIN_WIDTH;
  const arm = isWide && !isOutsideExperiment ? variant : null;
  if (variant === "control") return { variant, mode: "old", arm };
  if (isWide === null) return { variant, mode: "both", arm };
  return { variant, mode: isWide ? "new" : "old", arm };
};

export default useOverviewGalleryArm;
